import React, { useState, useEffect } from "react";
import logo from "../assets/logo.png";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../contexts/AuthContext';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import welcome9 from "./splashscreens/57.png";
import google from "../assets/icons/google.png"
import { useGoogleLogin } from '@react-oauth/google';
import { Capacitor } from "@capacitor/core";
import { canUseNativeBiometrics, checkBiometricAvailability, isBiometricUnlockEnabled } from '../lib/nativeBiometrics';

export default function SignIn({ onSignUp, onSignIn, onForgot }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isGoogleConsentOpen, setIsGoogleConsentOpen] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { signin, signinWithGoogle, user, isBootstrapping, refreshCompletionFlags } = useAuth();
  const isNativePlatform = Capacitor.isNativePlatform();

  const launchGoogleLogin = useGoogleLogin({
    flow: 'implicit',
    ux_mode: isNativePlatform ? 'redirect' : 'popup',
    redirect_uri: `${window.location.origin}/signin`,
    scope: 'openid email profile',
    onSuccess: async (tokenResponse) => {
      try {
        const result = await signinWithGoogle(tokenResponse?.access_token, 'access_token');
        toast.success(result?.created ? "Google account created successfully 🎉" : "Signed in with Google 🎉");
        onSignIn && onSignIn();
        navigate(await resolvePostAuthRoute());
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Google sign in failed. Please try again.");
      } finally {
        setIsGoogleLoading(false);
      }
    },
    onError: () => {
      setIsGoogleLoading(false);
      toast.error("Google sign in failed. Please try again.");
    },
    onNonOAuthError: (nonOAuthError) => {
      setIsGoogleLoading(false);
      if (nonOAuthError?.type === 'popup_closed') {
        toast.warning('Google popup was closed before completion.');
        return;
      }
      if (nonOAuthError?.type === 'popup_failed_to_open') {
        toast.error(isNativePlatform ? 'Google sign-in window could not be opened on this device. Please try again.' : 'Popup was blocked. Please allow popups and try again.');
        return;
      }
      toast.error('Google sign in could not be started. Please try again.');
    },
  });

  const resolvePostAuthRoute = async () => {
    try {
      const flags = await refreshCompletionFlags();
      const hasProfile = Boolean(flags?.hasProfile);
      const hasAssessment = Boolean(flags?.hasAssessment);

      if (!hasProfile) return '/profile-setup';
      if (!hasAssessment) return '/health-assessment';
      return '/home';
    } catch {
      const hasProfile = localStorage.getItem('HAS_PROFILE') === 'true';
      const hasAssessment = localStorage.getItem('HAS_ASSESSMENT') === 'true';
      if (!hasProfile) return '/profile-setup';
      if (!hasAssessment) return '/health-assessment';
      return '/home';
    }
  };

  useEffect(() => {
    if (isBootstrapping) return;
    if (!user) return;

    let active = true;
    (async () => {
      const route = await resolvePostAuthRoute();
      if (active) {
        navigate(route, { replace: true });
      }
    })();

    return () => {
      active = false;
    };
  }, [isBootstrapping, user, navigate]);

  useEffect(() => {
    setKeepSignedIn(isBiometricUnlockEnabled());
  }, []);

  useEffect(() => {
    let active = true;

    const loadBiometricAvailability = async () => {
      if (!canUseNativeBiometrics()) {
        if (active) setBiometricAvailable(false);
        return;
      }

      const result = await checkBiometricAvailability();
      if (active) {
        setBiometricAvailable(Boolean(result.ok && result.available));
      }
    };

    loadBiometricAvailability();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    try {
      if (localStorage.getItem('MOCK_AUTH') === 'true') {
        const mockUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (mockUser?.email) setEmail(mockUser.email);
        setPassword('mock-password');
      }
    } catch {}
  }, []);

  const handleSignIn = async (e) => {
    e.preventDefault();
    try {
      await signin({ email, password, enableBiometricUnlock: biometricAvailable && keepSignedIn });
      toast.success("Signed in successfully 🎉");
      onSignIn && onSignIn();
      navigate(await resolvePostAuthRoute());
    } catch (err) {
      const message = (err?.response?.data?.message || err?.response?.data?.error || '').toLowerCase();

      if (message.includes('password')) {
        toast.error("Password not correct.");
      } else if (err?.code === 'ECONNABORTED') {
        toast.error('Sign in timed out. Please try again in a few seconds.');
      } else if (!err?.response) {
        toast.error('Unable to reach the server. Check connection, then try again.');
      } else {
        toast.error(err?.response?.data?.message || err?.response?.data?.error || "Sign in failed. Please try again.");
      }
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      launchGoogleLogin();
    } catch {
      setIsGoogleLoading(false);
      toast.error('Google sign in could not be started. Please try again.');
    }
  };

  const handleGooglePrecheck = () => {
    if (isGoogleLoading) return;
    setIsGoogleConsentOpen(true);
  };

  const handleGoogleConsentContinue = () => {
    setIsGoogleConsentOpen(false);
    handleGoogleLogin();
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "22px 16px",
        backgroundImage: "var(--bg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}

    >
      {isGoogleConsentOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            backdropFilter: "blur(5px)",
            zIndex: 1001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div style={googleConsentCard}>
            <h4 style={googleConsentTitle}>Continue with Google</h4>
            <p style={googleConsentText}>
              Qureo will request access to your Google basic profile data: name, email address, and profile picture.
            </p>
            <p style={googleConsentSubtext}>
              This data is used only for secure sign in, account linking, and personalizing your care experience. Qureo never posts to your Google account.
            </p>
            <button type="button" onClick={() => navigate('/terms-policy')} style={googleConsentPolicyBtn}>
              Review Terms and Privacy Policy
            </button>
            <div style={googleConsentActions}>
              <button type="button" onClick={() => setIsGoogleConsentOpen(false)} style={googleCancelBtn}>
                Cancel
              </button>
              <button type="button" onClick={handleGoogleConsentContinue} style={googleContinueBtn}>
           Continue
              </button>
            </div>
          </div>
        </div>
      )}


      <div style = {{ width: "100%", maxWidth: "360px", background : "white", borderRadius: "22px", padding: "16px"  }}>
      <div style={{ width: "100%", maxWidth: "360px" }}>
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <h3 style={{ color: "#000000", fontSize: "28px", fontWeight: 800, lineHeight: 1.05, marginBottom: "8px" }}>
            Welcome back
          </h3>
          <p style={{ fontSize : "12px", color: "#6b7280", fontWeight: 500, margin: 0 }}>
            Sign in to your Qureo account
            <br />
            and manage your health.
          </p>
        </div>

        <form onSubmit={handleSignIn}>
          <div style={cardWrap}>
            <div style={{ marginBottom: "12px" }}>
              <label style={fieldLabel}>Email Address</label>
              <div style={inputWrapperMuted}>
                <span style={iconLeftMuted}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={iconInput}
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div style={{ marginBottom: "10px" }}>
              <label style={fieldLabel}>Password</label>
              <div style={inputWrapperMuted}>
                <span style={iconLeftMuted}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  style={iconInput}
                />
                <button type="button" onClick={() => setShowPassword((v) => !v)} style={eyeBtn}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
            <label style={{ fontSize: "13px", display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={keepSignedIn}
                onChange={(e) => setKeepSignedIn(e.target.checked)}
                style={{ accentColor: "var(--primary)", width: "15px", height: "15px", cursor: "pointer" }}
              />
              <span style={{ color: "#404756", fontWeight: 500 }}>
                {biometricAvailable ? "Enable biometric unlock" : "Keep me signed in"}
              </span>
            </label>

            <a
              href="/forgot-options"
              onClick={onForgot}
              style={{
                fontSize: "13px",
                color: "var(--primary)",
                textDecoration: "none",
                fontWeight: 700,
              }}
            >
              Forgot password?
            </a>
          </div>

          <button type="submit" style={primaryBtn}>
            Sign In
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: "8px" }}>
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>

          <div style={divider}>or</div>

          <button
            type="button"
            onClick={() => toast.info("Apple sign in not implemented yet")}
            style={{ ...socialBtn, opacity: 0.95 }}
          >
            <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg" alt="Apple" style={{ height: "18px" }} />
            Continue with Apple
          </button>

          <button
            type="button"
            onClick={handleGooglePrecheck}
            disabled={isGoogleLoading}
            style={{ ...socialBtn, opacity: isGoogleLoading ? 0.7 : 1 }}
          >
            <img src={google} alt="Google" style={{ height: "18px" }} />
            {isGoogleLoading ? "Connecting to Google..." : "Continue with Google"}
          </button>

          <button
            type="button"
            style={socialBtn}
          >
            <i className="fi fi-ss-circle-phone" style={{ color: "var(--primary)", fontSize: "18px" }}></i>
            Continue with phone number
          </button>

          <div style={{ textAlign: "center", fontSize: "13px", marginTop: "6px" }}>
            <span style={{ color: "#8a90a0" }}>Don't have an account?</span>
            <a
              href="/signup"
              onClick={onSignUp}
              style={{
                marginLeft: "6px",
                color: "var(--primary)",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              Sign up
            </a>
          </div>
        </form>
      </div>
</div>

      <ToastContainer />
    </div>
  );
}

/* 🔹 Shared Styles */
const cardWrap = {
  border: "1px solid #ececf5",
  borderRadius: "14px",
  background: "#fff",
  padding: "14px 12px",
  marginBottom: "14px",
  boxShadow: "0 1px 2px rgba(17, 24, 39, 0.04)",
};

const fieldLabel = {
  fontSize: "13px",
  fontWeight: 700,
  color: "#2f374d",
  marginBottom: "6px",
  display: "block",
};

const inputWrapperMuted = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  border: "1px solid #e4e5ee",
  borderRadius: "10px",
  background: "#fff",
  overflow: "hidden",
};

const iconLeftMuted = {
  display: "flex",
  alignItems: "center",
  paddingLeft: "10px",
  flexShrink: 0,
};

const iconInput = {
  flex: 1,
  padding: "12px 10px",
  border: "none",
  outline: "none",
  fontSize: "14px",
  background: "transparent",
  color: "#1f2937",
};

const eyeBtn = {
  display: "flex",
  alignItems: "center",
  paddingRight: "10px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  flexShrink: 0,
};

const primaryBtn = {
  width: "100%",
  padding: "14px",
  borderRadius: "12px",
  border: "none",
  background: "var(--primary)",
  color: "#fff",
  fontSize: "15px",
  fontWeight: 700,
  cursor: "pointer",
  marginBottom: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const divider = {
  textAlign: "center",
  fontSize: "13px",
  color: "#b0b5c2",
  margin: "8px 0 12px",
  fontWeight: 600,
};

const socialBtn = {
  width: "100%",
  height: "48px",
  borderRadius: "10px",
  border: "1px solid #e4e5ee",
  background: "#FFFFFF",
  fontSize: "15px",
  fontWeight: 700,
  color: "#111827",
  cursor: "pointer",
  marginBottom: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  transition: "all 0.2s ease",
};

const googleConsentCard = {
  width: "100%",
  maxWidth: "420px",
  background: "#fff",
  borderRadius: "16px",
  border: "1px solid #ececf5",
  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.22)",
  padding: "20px",
};

const googleConsentTitle = {
  margin: 0,
  marginBottom: "10px",
  color: "#1f2945",
  fontSize: "20px",
  fontWeight: 800,
};

const googleConsentText = {
  margin: 0,
  color: "#374151",
  fontSize: "14px",
  lineHeight: 1.5,
};

const googleConsentSubtext = {
  marginTop: "8px",
  marginBottom: "14px",
  color: "#6b7280",
  fontSize: "13px",
  lineHeight: 1.5,
};

const googleConsentPolicyBtn = {
  border: "none",
  background: "transparent",
  color: "var(--primary)",
  fontWeight: 700,
  fontSize: "13px",
  cursor: "pointer",
  padding: 0,
  marginBottom: "16px",
};

const googleConsentActions = {
  display: "flex",
  gap: "10px",
};

const googleCancelBtn = {
  flex: 1,
  height: "42px",
  borderRadius: "10px",
  border: "1px solid #e5e7eb",
  background: "#fff",
  color: "#374151",
  fontWeight: 700,
  cursor: "pointer",
};

const googleContinueBtn = {
  flex: 1.8,
  height: "42px",
  borderRadius: "10px",
  border: "none",
  background: "var(--primary)",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
};
